import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getExpertBalance,
  getConnectStatus,
  onboardExpert,
  getDashboardLink,
  listExpertEscrows,
  listPayouts,
  requestPayout,
} from '../../services/payments';
import type {
  ExpertBalance,
  ConnectStatusResponse,
  EscrowHold,
  Payout,
  OnboardExpertRequest,
} from '../../types/payment';
import {
  formatCurrency,
  getEscrowStatusLabel,
  getEscrowStatusColor,
  getPayoutStatusLabel,
  getPayoutStatusColor,
} from '../../types/payment';

export default function EarningsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [balance, setBalance] = useState<ExpertBalance | null>(null);
  const [connectStatus, setConnectStatus] = useState<ConnectStatusResponse | null>(null);
  const [escrows, setEscrows] = useState<EscrowHold[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);

  // UI state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Load Connect status first
      try {
        const status = await getConnectStatus();
        setConnectStatus(status);

        // Only load balance and other data if onboarded
        if (status.is_fully_onboarded) {
          const [balanceData, escrowsData, payoutsData] = await Promise.all([
            getExpertBalance(),
            listExpertEscrows(1, 10),
            listPayouts(1, 10),
          ]);
          setBalance(balanceData);
          setEscrows(escrowsData.escrows);
          setPayouts(payoutsData.payouts);
        }
      } catch (e: unknown) {
        // No connect account yet
        if ((e as { response?: { status?: number } })?.response?.status === 404) {
          setConnectStatus(null);
        } else {
          throw e;
        }
      }
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  }

  async function handleOnboard(data: OnboardExpertRequest) {
    try {
      setProcessing(true);
      const response = await onboardExpert(data);
      // Redirect to Stripe onboarding
      window.location.href = response.onboarding_url;
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to start onboarding');
    } finally {
      setProcessing(false);
    }
  }

  async function handleWithdraw() {
    if (!withdrawAmount) return;

    const amountCents = Math.round(parseFloat(withdrawAmount) * 100);
    if (amountCents < (balance?.min_withdrawal_cents || 1000)) {
      setError(`Minimum withdrawal is ${formatCurrency(balance?.min_withdrawal_cents || 1000)}`);
      return;
    }

    try {
      setProcessing(true);
      await requestPayout({ amount_cents: amountCents });
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      loadData(); // Refresh data
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to process withdrawal');
    } finally {
      setProcessing(false);
    }
  }

  async function openStripeDashboard() {
    try {
      const { dashboard_url } = await getDashboardLink();
      window.open(dashboard_url, '_blank');
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to get dashboard link');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not onboarded yet
  if (!connectStatus || !connectStatus.is_fully_onboarded) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">Earnings</h1>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold mb-2">Set Up Payouts</h2>
          <p className="text-gray-600 mb-6">
            Connect your bank account to receive payments for your work.
            We use Stripe for secure, fast payments.
          </p>

          {connectStatus && !connectStatus.is_fully_onboarded && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800">
                Your account setup is incomplete. Please complete the verification process.
              </p>
            </div>
          )}

          <button
            onClick={() => setShowOnboardingModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {connectStatus ? 'Continue Setup' : 'Get Started'}
          </button>
        </div>

        {/* Onboarding Modal */}
        {showOnboardingModal && (
          <OnboardingModal
            onClose={() => setShowOnboardingModal(false)}
            onSubmit={handleOnboard}
            processing={processing}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Earnings</h1>
        <button
          onClick={openStripeDashboard}
          className="text-blue-600 hover:text-blue-700"
        >
          Open Stripe Dashboard
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <BalanceCard
          label="Available"
          amount={balance?.available_cents || 0}
          currency={balance?.currency || 'EUR'}
          color="green"
        />
        <BalanceCard
          label="Pending"
          amount={balance?.pending_cents || 0}
          currency={balance?.currency || 'EUR'}
          color="yellow"
        />
        <BalanceCard
          label="Total Earned"
          amount={balance?.total_earned_cents || 0}
          currency={balance?.currency || 'EUR'}
          color="blue"
        />
        <BalanceCard
          label="Total Withdrawn"
          amount={balance?.total_withdrawn_cents || 0}
          currency={balance?.currency || 'EUR'}
          color="gray"
        />
      </div>

      {/* Withdraw Button */}
      {balance?.can_withdraw && (
        <div className="mb-8">
          <button
            onClick={() => setShowWithdrawModal(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Withdraw Funds
          </button>
          <p className="mt-2 text-sm text-gray-500">
            Minimum withdrawal: {formatCurrency(balance.min_withdrawal_cents)}
          </p>
        </div>
      )}

      {/* Pending Escrows */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Pending Payments</h2>
        {escrows.length === 0 ? (
          <p className="text-gray-500">No pending payments</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Your Share</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {escrows.map((escrow) => (
                  <tr key={escrow.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {escrow.description || 'Escrow Payment'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(escrow.amount_cents, escrow.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {formatCurrency(escrow.expert_amount_cents, escrow.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={escrow.status} getLabel={getEscrowStatusLabel} getColor={getEscrowStatusColor} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(escrow.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payout History */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Payout History</h2>
        {payouts.length === 0 ? (
          <p className="text-gray-500">No payouts yet</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arrival Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payout.amount_cents, payout.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={payout.status} getLabel={getPayoutStatusLabel} getColor={getPayoutStatusColor} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.bank_name ? `${payout.bank_name} ****${payout.bank_last4}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.arrival_date ? new Date(payout.arrival_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Withdraw Modal */}
      {showWithdrawModal && balance && (
        <WithdrawModal
          balance={balance}
          onClose={() => setShowWithdrawModal(false)}
          onSubmit={handleWithdraw}
          amount={withdrawAmount}
          setAmount={setWithdrawAmount}
          processing={processing}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface BalanceCardProps {
  label: string;
  amount: number;
  currency: string;
  color: 'green' | 'yellow' | 'blue' | 'gray';
}

function BalanceCard({ label, amount, currency, color }: BalanceCardProps) {
  const colorClasses = {
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    blue: 'bg-blue-50 border-blue-200',
    gray: 'bg-gray-50 border-gray-200',
  };

  return (
    <div className={`p-6 rounded-lg border ${colorClasses[color]}`}>
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold">{formatCurrency(amount, currency)}</p>
    </div>
  );
}

interface StatusBadgeProps<T extends string> {
  status: T;
  getLabel: (status: T) => string;
  getColor: (status: T) => string;
}

function StatusBadge<T extends string>({ status, getLabel, getColor }: StatusBadgeProps<T>) {
  const color = getColor(status);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    orange: 'bg-orange-100 text-orange-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses[color] || colorClasses.gray}`}>
      {getLabel(status)}
    </span>
  );
}

interface OnboardingModalProps {
  onClose: () => void;
  onSubmit: (data: OnboardExpertRequest) => void;
  processing: boolean;
}

function OnboardingModal({ onClose, onSubmit, processing }: OnboardingModalProps) {
  const [country, setCountry] = useState('IE');
  const [businessType, setBusinessType] = useState<'individual' | 'company'>('individual');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Set Up Payments</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="IE">Ireland</option>
            <option value="GB">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="NL">Netherlands</option>
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Type
          </label>
          <div className="space-y-2">
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="businessType"
                value="individual"
                checked={businessType === 'individual'}
                onChange={(e) => setBusinessType(e.target.value as 'individual' | 'company')}
                className="mr-3"
              />
              <div>
                <p className="font-medium">Individual</p>
                <p className="text-sm text-gray-500">For freelancers and sole traders</p>
              </div>
            </label>
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="businessType"
                value="company"
                checked={businessType === 'company'}
                onChange={(e) => setBusinessType(e.target.value as 'individual' | 'company')}
                className="mr-3"
              />
              <div>
                <p className="font-medium">Company</p>
                <p className="text-sm text-gray-500">For registered businesses</p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit({ country, business_type: businessType })}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={processing}
          >
            {processing ? 'Starting...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface WithdrawModalProps {
  balance: ExpertBalance;
  onClose: () => void;
  onSubmit: () => void;
  amount: string;
  setAmount: (amount: string) => void;
  processing: boolean;
}

function WithdrawModal({ balance, onClose, onSubmit, amount, setAmount, processing }: WithdrawModalProps) {
  const amountCents = amount ? Math.round(parseFloat(amount) * 100) : 0;
  const isValidAmount = amountCents >= balance.min_withdrawal_cents && amountCents <= balance.available_cents;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Withdraw Funds</h2>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">Available balance</p>
          <p className="text-2xl font-bold">{formatCurrency(balance.available_cents, balance.currency)}</p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Amount to withdraw
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500">{balance.currency === 'EUR' ? '\u20AC' : balance.currency}</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min={balance.min_withdrawal_cents / 100}
              max={balance.available_cents / 100}
              className="w-full p-3 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Minimum: {formatCurrency(balance.min_withdrawal_cents, balance.currency)}
          </p>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            disabled={processing || !isValidAmount}
          >
            {processing ? 'Processing...' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );
}
